import type { RouteObject } from 'react-router';

import { Suspense } from 'react';
import { Outlet } from 'react-router';

import lazyWithReload from 'src/utils/lazyWithReload';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';
import { PermissionWrapper } from 'src/pages/auth/hooks/permissionWrapper';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../../../routes/hooks';

// ----------------------------------------------------------------------

const WorkflowListPage = lazyWithReload(() => import('src/pages/automation/workflow/list'));
const WorkflowBuilderPage = lazyWithReload(() => import('src/pages/automation/workflow/builder'));
const AgentListPage = lazyWithReload(() => import('src/pages/automation/agents/list'));
const AgentDetailPage = lazyWithReload(() => import('src/pages/automation/agents/detail'));
const AgentWizardPage = lazyWithReload(
  () => import('src/pages/automation/agents/wizard/AgentWizard')
);
const AgentAnalyticsPage = lazyWithReload(
  () => import('src/pages/automation/agents/analytics/agent-analytics-view')
);
const AgentManagedUIPage = lazyWithReload(() => import('src/pages/automation/agent-managed-ui'));
const AgentLearningQueuePage = lazyWithReload(
  () => import('src/pages/automation/agents/learning/LearningQueuePage')
);
const AgentKnowledgeGapsPage = lazyWithReload(
  () => import('src/pages/automation/agents/learning/KnowledgeGapsPage')
);

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

// Helper to wrap a component with PermissionWrapper
const withPermission = (
  module: string | string[],
  Component: React.LazyExoticComponent<React.ComponentType<any>>
) => (
  <PermissionWrapper module={module}>
    <Component />
  </PermissionWrapper>
);

export const automationRoutes: RouteObject[] = [
  {
    path: 'automation',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { index: true, element: withPermission('workflow', WorkflowListPage) },
      { path: 'workflows', element: withPermission('workflow', WorkflowListPage) },
      { path: 'workflows/new', element: withPermission('workflow', WorkflowBuilderPage) },
      { path: 'workflows/:id/edit', element: withPermission('workflow', WorkflowBuilderPage) },
      { path: 'agents', element: withPermission('ai_agents', AgentListPage) },
      { path: 'agents/analytics', element: withPermission('ai_analytics', AgentAnalyticsPage) },
      { path: 'agents/learning-queue', element: withPermission('ai_agents', AgentLearningQueuePage) },
      { path: 'agents/knowledge-gaps', element: withPermission('ai_agents', AgentKnowledgeGapsPage) },
      { path: 'agents/new', element: withPermission('ai_agents', AgentWizardPage) },
      { path: 'agents/:agentId', element: withPermission('ai_agents', AgentDetailPage) },
      { path: 'agents/ui-demo', element: withPermission('ai_agents', AgentManagedUIPage) },
    ],
  },
];
